import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, OnInit, computed } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCog, faPlus, faHandshake, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { MatchesListComponent, MatchGroup, MatchItem, MatchAction } from './matches-list/matches-list.component';
import { LoadMatchService } from './load-match.service';
import { LoadMatch, LoadMatchStatus } from './load-match.model';
import { AuthService } from '../auth/auth.service';
import { CustomerService } from '../customer/customer.service';
import { LoadRequestService } from '../loads/load-request.service';
import { LoadAvailabilityService } from '../availabilities/load-availability.service';
import { LoadRequest } from '../loads/load-request.model';
import { LoadAvailability } from '../availabilities/load-availability.model';
import { map, switchMap, of, forkJoin, catchError } from 'rxjs';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { CurrencyResolverService } from '../services/currency-resolver.service';

@Component({
  selector: 'app-matches',
  standalone: true,
  templateUrl: './matches.component.html',
  styleUrls: ['./matches.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonModule, FontAwesomeModule, MatchesListComponent, PaginatorModule, RouterModule]
})
export class MatchesComponent implements OnInit {
  private router = inject(Router);
  private loadMatchService = inject(LoadMatchService);
  private authService = inject(AuthService);
  private customerService = inject(CustomerService);
  private loadRequestService = inject(LoadRequestService);
  private loadAvailabilityService = inject(LoadAvailabilityService);
  private currencyResolver = inject(CurrencyResolverService);

  readonly icons = {
    cog: faCog,
    handshake: faHandshake,
    listCheck: faListCheck,
    plus: faPlus
  };

  private matches = signal<LoadMatch[]>([]);
  private requests = signal<Record<number, LoadRequest>>({});
  private availabilities = signal<Record<number, LoadAvailability>>({});

  loading = signal<boolean>(false);
  
  // Pagination
  rows = signal(10);
  first = signal(0);
  totalRecords = signal(0);
  page = signal(0);

  matchGroups = computed<MatchGroup[]>(() => {
    const list = this.matches();
    const reqs = this.requests();
    const avails = this.availabilities();

    const items = list.map(m => this.transformToMatchItem(m, reqs[m.loadRequestId], avails[m.loadAvailabilityId]));

    const groups: MatchGroup[] = [
      {
        key: 'all',
        label: 'All',
        summaryLabel: 'Total Matches',
        matches: items
      }
    ];

    // Create groups for each status that has items
    Object.values(LoadMatchStatus).forEach(status => {
      const filtered = items.filter(i => i.status === status);
      if (filtered.length > 0) {
        groups.push({
          key: status.toLowerCase(),
          label: status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' '),
          summaryLabel: status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' '),
          matches: filtered
        });
      }
    });

    return groups;
  });

  ngOnInit() {
    this.refresh();
  }

  refresh() {
    this.loading.set(true);
    this.authService.userData.pipe(
      switchMap(user => {
        if (!user) return of(null);
        return this.customerService.getCustomerByFirebaseId(user.uid);
      })
    ).subscribe({
      next: (response: any) => {
        const customer = response?.data;
        if (customer?.company?.id) {
          const params: any = {
            shipperCompanyId: customer.company.id
          };
          if (customer.company.transportationAgencyId) {
            params.transportationAgencyId = customer.company.transportationAgencyId;
          }

          this.loadMatchService.search(params, this.page(), this.rows()).subscribe({
            next: (page) => {
              const matches = page.content;
              this.matches.set(matches);
              this.totalRecords.set(page.totalElements);
              this.fetchRelatedEntities(matches);
            },
            error: () => this.loading.set(false)
          });
        } else {
          this.loading.set(false);
        }
      },
      error: () => this.loading.set(false)
    });
  }

  onPageChange(event: PaginatorState) {
    this.first.set(event.first ?? 0);
    this.rows.set(event.rows ?? 10);
    this.page.set(event.page ?? 0);
    this.refresh();
  }

  private fetchRelatedEntities(matches: LoadMatch[]) {
    if (matches.length === 0) {
      this.loading.set(false);
      return;
    }

    const requestIds = [...new Set(matches.map(m => m.loadRequestId).filter((id): id is number => !!id))];
    const availabilityIds = [...new Set(matches.map(m => m.loadAvailabilityId).filter((id): id is number => !!id))];

    const requestCalls = requestIds.map(id => this.loadRequestService.get(id).pipe(catchError(() => of(null))));
    const availabilityCalls = availabilityIds.map(id => this.loadAvailabilityService.get(id).pipe(catchError(() => of(null))));

    forkJoin({
      requests: requestCalls.length ? forkJoin(requestCalls) : of([]),
      availabilities: availabilityCalls.length ? forkJoin(availabilityCalls) : of([])
    }).subscribe({
      next: (result) => {
        const reqMap: Record<number, LoadRequest> = {};
        result.requests.forEach(r => { if (r) reqMap[r.id] = r; });
        this.requests.set(reqMap);

        const availMap: Record<number, LoadAvailability> = {};
        result.availabilities.forEach(a => { if (a) availMap[a.id] = a; });
        this.availabilities.set(availMap);

        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  onActionClick(event: { match: MatchItem, action: MatchAction }) {
    if (event.action.label === 'View Details') {
      this.router.navigate(['/matches', event.match.id]);
    }
  }

  private transformToMatchItem(m: LoadMatch, req?: LoadRequest, avail?: LoadAvailability): MatchItem {
    const currency = this.currencyResolver.effectiveCurrencyCode(req?.currencyCode);
    return {
      id: m.id,
      matchNumber: m.matchNumber || `MTCH-${m.id}`,
      loadRequestNumber: m.loadReferenceNumber || req?.referenceNumber || 'N/A',
      loadAvailabilityNumber: m.availabilityReferenceNumber || avail?.referenceNumber || 'N/A',
      status: m.status,
      distanceAway: avail?.corridorOrigin ? `${avail.corridorOrigin} – ${avail.corridorDestination}` : 'N/A',
      eta: m.createdAt ? new Date(m.createdAt).toLocaleDateString() : 'N/A',
      offerAmount: m.proposedRate ? `${currency} ${m.proposedRate.toLocaleString()}` : 'N/A',
      ratePerMile: req && m.proposedRate ? `${currency} ${((m.proposedRate) / (req.totalDistanceInKm ? req.totalDistanceInKm * 0.621 : 1)).toFixed(2)}/mi` : 'N/A',
      badges: ['Score 85'],
      equipment: [req?.equipmentType || avail?.equipmentType || 'Dry Van'],
      avatar: {
        label: 'C',
        styleClass: 'bg-blue-100 text-blue-700'
      },
      actions: [
        { label: 'View Details', styleClass: 'p-button-outlined' }
      ]
    };
  }
}
